import { useRef, useState } from "react"

// ONE home for the Cloudflare Turnstile widget's wiring (act:1ca3011b): how the
// token is held, what happens when the widget fails, and how a spent token is
// replaced. Consumed by the sign-up funnel's contact step (QualForm.jsx) today;
// the case file's two doors (RequestLink.jsx, YourFile.jsx) still mount the
// widget raw with `onSuccess` alone and are the follow-on, act:ef2683f9 —
// spec/frontend/funnel_turnstile_wiring_spec.rb pins them as the two known
// exceptions until they convert.
//
// WHAT HAPPENED, and what each piece below answers (2026-08-27, a paid Meta-ad
// visitor in the Facebook in-app browser, refused at the contact step, gone):
//
//   * A token is SINGLE-USE and lives 300 s. After the server refuses one, the
//     widget still holds it, so "Please try again" re-posts the spent token and
//     is refused again as `timeout-or-duplicate`. → `spend(postedToken)`,
//     called by the page when the server re-renders it with an error: it
//     resets the widget UNLESS a fresh token has landed since the post (a slow
//     challenge that completed while the refusal was in flight is the retry,
//     not a casualty of it).
//   * A tap before the token has landed posts an empty one
//     (`missing-input-response`). → `awaitToken()` waits a bounded few seconds
//     for the widget (our own poll, see there), then posts REGARDLESS. The server is the only gate, on
//     purpose: the operator's standing rule is that a real claimant blocked
//     costs more than a forged request let through, and a submit button that
//     never enables in a browser where the widget never finishes would be a
//     refusal nobody can count. Disabling until the token arrives — which is
//     Cloudflare's reference example — was considered and declined for exactly
//     that reason (cabinet CP1, 2026-08-27).
//   * The widget can fail on the CLIENT — script blocked, an unsupported
//     WebView, an interaction timeout, an expired token — and none of that ever
//     reaches the server (Cloudflare also throws an uncaught exception when no
//     error callback is wired). → every failure path clears the token and
//     records `failure` for the page to show something. The one failure the
//     widget cannot report itself — its script never loads, or loads and never
//     calls back — is caught two ways: the script tag's own `onerror`, and
//     `awaitToken` giving up with nothing to show for it (`absent`).
//   * Cloudflare's error callback fires on EVERY auto-retry of the same fault
//     (every 8 s, for as long as the page is open). → `onFailure` is called
//     once per (outcome, code) per mount, and only for the outcomes that are
//     STUCK — expiry and interaction timeouts self-heal and would be noise.
//
// Defaults left alone, on purpose: `retry: auto` (8 s interval), `refreshExpired:
// auto`, `appearance: always` — a visible widget is the one "something is
// happening" cue a phone visitor gets while the check runs.
//
// No memoization: the wrapper reads its callbacks through a ref
// (`rerenderOnCallbackChange` is false), and nothing identity-sensitive
// receives the returned functions, so `useCallback` would be a claim with
// nothing behind it.

// How long a submit waits for a token that has not landed before posting
// without one (bullet 2 above). The widget normally answers in 1–3 s; the page
// shows the wait ("One moment…" on the disabled button), so it is not silent.
export const TOKEN_GRACE_MS = 5000
const TOKEN_POLL_MS = 100

// The ways the widget can fail on the client, and which of them are STUCK
// (bullet 4 above): a load error, an unsupported browser, or a widget that
// never produced anything (`absent`) will not recover on its own, and the
// visitor should know they can still submit. Consumers read `failure.stuck`
// and decide only the COPY, which is legitimately per-mount ("tap Sign Up" vs
// "email me my link").
export const OUTCOMES = Object.freeze({
  error: "error",
  expired: "expired",
  timeout: "timeout",
  unsupported: "unsupported",
  absent: "absent",
})
const STUCK_OUTCOMES = new Set([OUTCOMES.error, OUTCOMES.unsupported, OUTCOMES.absent])

export function useTurnstile({ siteKey, onFailure }) {
  const ref = useRef(null)
  // The token is a REF, not state, and it is not returned: nothing renders
  // from it (the widget owns its own display), and the one sanctioned reader is
  // `awaitToken` at submit time. Returning it would invite the very
  // `disabled={!token}` gate the operator declined.
  const tokenRef = useRef("")
  // { outcome, code, stuck } of the most recent client-side failure, or null.
  // `code` is Cloudflare's client error code (e.g. "110600") when given one.
  // State for the page to render from, plus a ref twin for the callbacks that
  // run outside a render (`awaitToken` reads it mid-wait).
  const [failure, setFailure] = useState(null)
  const failureRef = useRef(null)
  // (outcome, code) pairs already reported for this mount — see the header.
  const reported = useRef(new Set())

  const hold = (t) => {
    tokenRef.current = t
  }

  const record = (detail) => {
    failureRef.current = detail
    setFailure(detail)
  }

  const fail = (outcome, code) => {
    hold("")
    const detail = {
      outcome,
      code: code == null || code === "" ? null : String(code),
      stuck: STUCK_OUTCOMES.has(outcome),
    }
    record(detail)
    const key = `${detail.outcome}:${detail.code}`
    if (detail.stuck && !reported.current.has(key) && typeof onFailure === "function") {
      reported.current.add(key)
      try {
        onFailure(detail)
      } catch {
        // reporting a failure must never break the form it reports on
      }
    }
  }

  const widgetProps = {
    ref,
    siteKey,
    onSuccess: (t) => {
      hold(t)
      record(null)
    },
    onError: (code) => fail(OUTCOMES.error, code),
    onExpire: () => fail(OUTCOMES.expired),
    onTimeout: () => fail(OUTCOMES.timeout),
    onUnsupported: () => fail(OUTCOMES.unsupported),
    // The widget's SCRIPT failing to load fires none of the callbacks above —
    // the wrapper attaches the script tag's onerror only when asked.
    scriptOptions: { onError: () => fail(OUTCOMES.error, "script-load") },
  }

  // Drop the held token and ask the widget for a fresh challenge. Safe before
  // the widget has rendered (the wrapper warns and returns) and after.
  const reset = () => {
    hold("")
    try {
      ref.current?.reset()
    } catch {
      // a widget that cannot reset is a widget the next submit will be refused
      // on — and counted. Never throw out of a form handler.
    }
  }

  // The server refused the token we posted. Reset so the retry carries a
  // fresh challenge — EXCEPT in two cases where a reset would throw away the
  // very thing the retry needs: we posted NOTHING (an empty token is never
  // consumed by siteverify, and in a slow WebView the challenge is most likely
  // still running — let it finish rather than restart its clock), or the
  // widget has produced a DIFFERENT token since the post (a slow challenge
  // that completed while the refusal was in flight). A loaded widget that
  // sits silent forever is the one case this leaves un-reset; `retry: auto`
  // and the visitor's next tap cover it, and it is counted either way.
  const spend = (postedToken) => {
    if (!postedToken) return
    const current = tokenRef.current
    if (current && current !== postedToken) return
    reset()
  }

  // The token to post: the one already held, else whatever the widget produces
  // within `ms`, else "" — and the caller posts either way. This is OUR poll
  // over `tokenRef` (set by the widget's success callback), not the wrapper's
  // `getResponsePromise`: that one short-circuits on a solved-then-expired
  // widget (it never clears its solved flag on expiry, so the auto-refresh in
  // progress gets no wait at all) and never fully stops polling once it has
  // timed out. A widget already known to be stuck is not waited on.
  const awaitToken = async (ms = TOKEN_GRACE_MS) => {
    if (tokenRef.current) return tokenRef.current
    if (failureRef.current?.stuck) return ""
    for (let waited = 0; waited < ms; waited += TOKEN_POLL_MS) {
      await new Promise((resolve) => setTimeout(resolve, TOKEN_POLL_MS))
      if (tokenRef.current) return tokenRef.current
      if (failureRef.current?.stuck) return ""
    }
    // Nothing arrived and nothing was reported — not even during the wait:
    // the widget never produced a token and never said why. Name it, so the
    // page can show something and the failure has a dataset. A failure the
    // widget DID report mid-wait keeps its own outcome and code.
    if (!failureRef.current) fail(OUTCOMES.absent)
    return ""
  }

  return { failure, widgetProps, reset, spend, awaitToken }
}
